import { Switch } from '@advanza/advanza_generic'
import { Col, Row } from '@advanza/grid'
import { InputField } from '@advanza/input'
import { changeEntity } from '@advanza/redux_entity'
import { Button, Divider, Icon, LoadingDots } from '@advanza/ui'
import { saveCreditLimits, saveService } from 'actions/services'
import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import { useState } from 'react'

const DEFAULT_CREDIT_LIMITS = [250, 500, 1000, 2000, 4000]

const ServiceCreditLimits = ({ serviceId }) => {
    const dispatch = useAppDispatch()
    const [isSaving, setIsSaving] = useState(false)
    const [newLimit, setNewLimit] = useState('')

    const service = useAppSelector((state) => state.services.entities.services?.[serviceId] || {})
    const creditLimits = (service.services_credit_limits || [])
        .map((item) => item.credit_limit)
        .sort((a, b) => a - b)

    const displayLimits = creditLimits.length > 0 ? creditLimits : DEFAULT_CREDIT_LIMITS
    const isUsingDefaults = creditLimits.length === 0

    const handleToggleEnableCreditLimits = () => {
        dispatch(
            changeEntity({
                store: 'services',
                name: 'services',
                key: serviceId,
                diff: { enable_credit_limits: !service.enable_credit_limits },
            }),
        )
        dispatch(saveService(serviceId, ['enable_credit_limits']))
    }

    const handleAddLimit = () => {
        const limitValue = parseInt(newLimit, 10)
        if (isNaN(limitValue) || limitValue <= 0) {
            return
        }

        if (creditLimits.includes(limitValue)) {
            return
        }

        const newLimits = [...creditLimits, limitValue].sort((a, b) => a - b)
        setIsSaving(true)
        dispatch(saveCreditLimits(serviceId, newLimits)).finally(() => {
            setIsSaving(false)
            setNewLimit('')
        })
    }

    const handleRemoveLimit = (limitToRemove) => {
        const newLimits = creditLimits.filter((limit) => limit !== limitToRemove)
        setIsSaving(true)
        dispatch(saveCreditLimits(serviceId, newLimits)).finally(() => {
            setIsSaving(false)
        })
    }

    const handleResetToDefaults = () => {
        setIsSaving(true)
        dispatch(saveCreditLimits(serviceId, [])).finally(() => {
            setIsSaving(false)
        })
    }

    return (
        <div>
            <Row middle="xs">
                <Col x>
                    <Switch
                        checked={service.enable_credit_limits !== false}
                        onChange={handleToggleEnableCreditLimits}
                    />
                </Col>
                <Col x>
                    <b>Enable credit limits</b>
                </Col>
            </Row>

            {service.enable_credit_limits !== false && (
                <>
                    <Divider m />
                    <div>
                        <b>Credit Limits (€)</b>
                        {isUsingDefaults && (
                            <span style={{ marginLeft: 8, color: '#888', fontSize: 12 }}>
                                (using defaults)
                            </span>
                        )}
                    </div>
                    <Divider s />

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {displayLimits.map((limit) => (
                            <div
                                key={limit}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    background: isUsingDefaults ? '#f0f0f0' : '#e3f2fd',
                                    padding: '4px 8px',
                                    borderRadius: 4,
                                }}>
                                <span>€{limit.toLocaleString()}</span>
                                {!isUsingDefaults && (
                                    <button
                                        style={{
                                            marginLeft: 4,
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: 2,
                                        }}
                                        onClick={() => handleRemoveLimit(limit)}
                                        disabled={isSaving}>
                                        <Icon name="close" style={{ fontSize: 14 }} />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>

                    <Divider m />

                    <Row middle="xs">
                        <Col x>
                            <InputField
                                type="number"
                                placeholder="Add new limit (€)"
                                value={newLimit}
                                onChange={(e) => setNewLimit(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleAddLimit()}
                                style={{ width: 150 }}
                            />
                        </Col>
                        <Col x>
                            <Button onClick={handleAddLimit} disabled={isSaving || !newLimit}>
                                {isSaving ? <LoadingDots /> : 'Add'}
                            </Button>
                        </Col>
                        {!isUsingDefaults && (
                            <Col x>
                                <Button
                                    name="text"
                                    onClick={handleResetToDefaults}
                                    disabled={isSaving}>
                                    Reset to defaults
                                </Button>
                            </Col>
                        )}
                    </Row>
                </>
            )}
        </div>
    )
}

export default ServiceCreditLimits
